// Brand registry + active-brand resolution.
//
// The widget is one bundle that serves every Bond brand. The active brand is
// chosen at boot by the `?brand=` query param (set by embed.js), falling back to
// the build-time VITE_DEFAULT_BRAND, then to garcia. This keeps the live
// bond-booking-widget (Garcia) unchanged while new brands are pure additions.

import type { BrandConfig } from "./types";
import { garcia } from "./garcia";
import { smileLee } from "./smile-lee";
import { newAge } from "./new-age";
import { lasVegas } from "./las-vegas";
import { pobanz } from "./pobanz";
import { freeman } from "./freeman";
import { allen } from "./allen";
import { braces520 } from "./braces520";
import { johnson } from "./johnson";
import { stormberg } from "./stormberg";
import { jtOrthodontics } from "./jt-orthodontics";
import { siliconValley } from "./silicon-valley";
import { berro } from "./berro";
import { postFalls } from "./post-falls";
import { curtis } from "./curtis";
import { adventure } from "./adventure";
import { richardChan } from "./richard-chan";
import { yang } from "./yang";
import { kirk } from "./kirk";
import { beglin } from "./beglin";
import { kenworthy } from "./kenworthy";

export const BRANDS: Record<string, BrandConfig> = {
  [garcia.slug]: garcia,
  [smileLee.slug]: smileLee,
  [newAge.slug]: newAge,
  [lasVegas.slug]: lasVegas,
  [pobanz.slug]: pobanz,
  [freeman.slug]: freeman,
  [allen.slug]: allen,
  [braces520.slug]: braces520,
  [johnson.slug]: johnson,
  [stormberg.slug]: stormberg,
  [jtOrthodontics.slug]: jtOrthodontics,
  [siliconValley.slug]: siliconValley,
  [berro.slug]: berro,
  [postFalls.slug]: postFalls,
  [curtis.slug]: curtis,
  [adventure.slug]: adventure,
  [richardChan.slug]: richardChan,
  [yang.slug]: yang,
  [kirk.slug]: kirk,
  [beglin.slug]: beglin,
  [kenworthy.slug]: kenworthy,
};

// Dual-context default: in Vite-processed code the exact import.meta.env.VITE_*
// reference is statically replaced at build time; under plain node (vite.config's
// build-time head generation imports this registry) import.meta.env is undefined,
// the access throws, and we read the same variable from process.env instead.
function envDefaultBrand(): string {
  try {
    return (import.meta.env.VITE_DEFAULT_BRAND as string | undefined)?.trim().toLowerCase() || "garcia";
  } catch {
    const p = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
    return (p?.env?.VITE_DEFAULT_BRAND ?? "").trim().toLowerCase() || "garcia";
  }
}
const DEFAULT_BRAND_SLUG = envDefaultBrand();

/**
 * Whether a brand's consultation is free. Free is the default across Bond, so
 * only a brand that charges opts out (see BrandConfig.consultIsFree). Both the
 * runtime UI and the build-time head generator go through here, so the emitted
 * metadata and the rendered copy can never disagree.
 */
export const isConsultFree = (brand: BrandConfig): boolean => brand.consultIsFree !== false;

/** Resolve a brand by slug, falling back to the build-time default, then garcia. */
export function resolveBrand(slug?: string | null): BrandConfig {
  const key = (slug || "").trim().toLowerCase();
  return BRANDS[key] || BRANDS[DEFAULT_BRAND_SLUG] || garcia;
}

/**
 * Per-site brand from the hostname, so one bundle deployed to N hosting sites
 * shows the right brand on the bare URL. Sites are named "<slug>-booking"
 * (e.g. smile-lee-booking.web.app). Garcia's site is "bond-booking-widget",
 * which matches nothing here and correctly falls through to the default.
 */
function brandSlugFromHost(): string | null {
  try {
    const host = window.location.hostname.toLowerCase();
    // Garcia's site predates the <slug>-booking convention; map it explicitly
    // so NO live site depends on the build-time default brand being right.
    if (host.startsWith("bond-booking-widget")) return "garcia";
    for (const slug of Object.keys(BRANDS)) {
      if (host === slug || host.startsWith(slug + "-booking") || host.startsWith(slug + ".")) return slug;
    }
  } catch {
    /* no window */
  }
  return null;
}

function detectActiveBrand(): BrandConfig {
  let qpBrand: string | null = null;
  try {
    qpBrand = new URLSearchParams(window.location.search).get("brand");
  } catch {
    /* no window (tests) -> default */
  }
  // Precedence: explicit ?brand= -> hostname (per-site) -> VITE_DEFAULT_BRAND -> garcia.
  const explicit = qpBrand && BRANDS[qpBrand.trim().toLowerCase()] ? qpBrand : null;
  return resolveBrand(explicit ?? brandSlugFromHost());
}

/** The brand for this widget instance. Resolved once at module load. */
export const ACTIVE_BRAND: BrandConfig = detectActiveBrand();

export type { BrandConfig, LocationDisplay, BrandTheme } from "./types";
